import { PlatformLocation } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { FeeDisclaimerService } from 'app/shared/services/fee-disclaimer.service';
import { PassDataService } from '../../mega-exchange/services/pass-data.service';
import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { UserService } from '../../shared/services/user.service';
import type { CalculateFeeMegaResponse } from '../services/interfaces/calculate-fee-mega-response';
import type { CdxOriginRequest } from '../services/interfaces/commit-mega-request';
import type { CommitMegaResponse } from '../services/interfaces/commit-mega-response';
import type { MegaErrors } from '../services/interfaces/mega-error';
import { MegaTradeNavigationService } from '../services/mega-trade-navigation.service';
import { MegasService } from '../services/megas.service';

@Component({
  standalone: false,
  selector: 'invd-review-and-commit',
  templateUrl: './review-and-commit.component.html',
  styleUrls: ['./review-and-commit.component.scss'],
})
export class ReviewAndCommitComponent implements OnInit, AfterViewInit, OnDestroy {
  public calcFeeRes: CalculateFeeMegaResponse;
  public feeTicks: string;
  public fee: string;
  public megaType = '';
  public showModelOne = false;
  public showModelTwo = false;

  public showBanner = false;
  public messageBody = '';

  public destinationUrl = '/';
  public isAllowToNavigateAway = false;
  public displayNavigateAwayPopup = false;

  public isTacAccept = false;
  public warningMessage: any = { show: false, message: '' };
  public isEditMegaFlow = false;
  public poolNumber: number;
  public cusip = '';
  public revisionNumber: number;

  public cancelBtnText: string;
  public showPricingWindow = false;
  public featuretoggle: any;
  public showEarlyCloseLegend = false;
  public megaPricingUpdateList: any;
  public showEarlyCloseLegendPricingSummary = false;
  public isCommitButtonClicked = false;
  public goBackAndEditLinkClicked = false;

  public showDisclaimer = false;
  public disclaimerMessage = '';

  private location = inject(PlatformLocation);
  private router = inject(Router);
  private passDataService = inject(PassDataService);
  private megaService = inject(MegasService);
  private userService = inject(UserService);
  private feeDisclaimerService = inject(FeeDisclaimerService);
  private megaNavService = inject(MegaTradeNavigationService);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private cdr = inject(ChangeDetectorRef);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = true;
  }

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.calcFeeRes = this.passDataService.getCalculateFeeMegaResponse();
    this.showEarlyCloseLegend =
      this.calcFeeRes && this.calcFeeRes.infoMessage !== null ? true : false;
    this.megaPricingUpdateList = this.calcFeeRes && this.calcFeeRes.megaPricingInfo[0].megaList;
    this.isEditMegaFlow = this.passDataService.getEditMegaFlow();
    if (this.isEditMegaFlow) {
      this.poolNumber = this.passDataService.getpoolNumberEditMegaFlow();
      this.cusip = this.passDataService.getCUSIPEditMegaFlow();
      this.revisionNumber = this.passDataService.getRevisionNumber();
      this.passDataService.setEditMegaFlow(true);
    } else {
      this.revisionNumber = null;
    }
    this.megaType = this.calcFeeRes.megaType;
    if (this.calcFeeRes.feeTicks !== null) {
      this.feeTicks = this.calcFeeRes.feeTicks.toFixed(3) + ' / 32';
    } else {
      this.fee = this.calcFeeRes.feeDollars.toString();
    }
    if (this.calcFeeRes && this.calcFeeRes.infoMessage && this.calcFeeRes.infoMessage !== null) {
      this.displayWarningMessage(this.calcFeeRes.infoMessage);
    }
    this.updateCancelBtnText();
    this.featuretoggle = this.userService.toggleFeatures;
    this.lookupForEarlyCloseFlag();
    this.checkDisclaimerStatus();
  }

  private updateCancelBtnText(): void {
    if (this.isEditMegaFlow) {
      this.cancelBtnText = StringConstants.MEGA_REVIEW_AND_COMMIT_SCREEN_CANCEL_EDIT_BTN_TEXT;
    } else {
      this.cancelBtnText = StringConstants.MEGA_REVIEW_AND_COMMIT_SCREEN_CANCEL_BTN_TEXT;
    }
  }

  public commitBtnClick(): void {
    if (!this.isEditMegaFlow) {
      this.showModelTwo = true;
      this.showModelOne = false;
    } else {
      this.showModelOne = false;
      this.showModelTwo = false;
      this.navigatePageTo('/mega-trade/committed-mega');
    }
  }

  ngAfterViewInit() {
    this.navigationController();
  }

  ngOnDestroy() {
    if (!this.passDataService.getMegaFlowGoBackAndEditLink()) {
      this.passDataService.setMegaRepopulateBundle(null);
    }
    this.passDataService.setCalculateFeeMegaResponse(null);
    this.passDataService.setMegaFlowGoBackAndEditLink(null);
  }

  public goBack(): void {
    this.passDataService.setMegaFlowGoBackAndEditLink(true);
    this.location.back();
  }

  public navigatePageTo(pageURL) {
    this.router.navigate([pageURL]);
    this.passDataService.setMegaRepopulateBundle(null);
    this.passDataService.setEditMegaFlow(false);
    this.isAllowToNavigateAway = true;
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('enter-details') > 0) {
          this.isAllowToNavigateAway = true;
        } else if (event.url.indexOf('error') > 0) {
          this.passDataService.setMegaRepopulateBundle(null);
          this.passDataService.setEditMegaFlow(false);
          this.isAllowToNavigateAway = true;
        } else if (event.url.indexOf('committed-megas') > 0) {
          this.passDataService.setEditMegaFlow(false);
          this.isAllowToNavigateAway = true;
        } else {
          this.navigateAwayPopupTrigger();
          this.isAllowToNavigateAway = false;
          this.destinationUrl = event.url;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
  }

  public navigateAwayPopupTrigger() {
    this.displayNavigateAwayPopup = true;
  }

  public megaCommitButtonClicked(): void {
    this.isCommitButtonClicked = true;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    const commitRequest = {} as CdxOriginRequest;
    commitRequest.poolNumber = this.poolNumber;
    commitRequest.megaType = this.calcFeeRes.megaType;
    commitRequest.prefixCode = this.calcFeeRes.prefixCode;
    commitRequest.tradeAmount = this.calcFeeRes.tradeAmount;
    commitRequest.tradeSettlementDate = this.calcFeeRes.tradeSettlementDate;
    commitRequest.sfFixedCoupon = this.calcFeeRes.sfFixedCoupon;
    commitRequest.sfARMSubType = this.calcFeeRes.sfARMSubType;
    commitRequest.mfHighCoupon = this.calcFeeRes.mfHighCoupon;
    commitRequest.mfLowCoupon = this.calcFeeRes.mfLowCoupon;
    commitRequest.feeTicks = this.calcFeeRes.feeTicks;
    commitRequest.feeDollars = this.calcFeeRes.feeDollars;
    commitRequest.commitConfirmed = true;
    commitRequest.isCollateralAnalyzer = false;

    commitRequest.tradeRevisionNumber = this.revisionNumber;
    commitRequest.packageRevisionNumber = this.calcFeeRes.packageRevisionNumber;

    this.megaService.commit(commitRequest).subscribe({
      next: (data) => this.handleCommitServiceResponseSuccess(data as CommitMegaResponse),
      error: (error) => this.handleCommitServiceResponseFailed(error),
    });
  }

  private handleCommitServiceResponseSuccess(commitResponse: CommitMegaResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.megaNavService.canNavigate = true;
    this.passDataService.setCommitMegaResponse(commitResponse);

    if (this.isEditMegaFlow) {
      this.passDataService.setEditMegaFlow(true);
    }

    this.cdr.markForCheck();
    this.router.navigate(['mega-trade/enter-details/confirmation']);
  }

  private handleCommitServiceResponseFailed(errors: any) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    const errorObject: MegaErrors = errors.error;
    this.showWarningMessage(true, errorObject.messages[0].errorMessage);
    this.cdr.markForCheck();
  }

  showWarningMessage(show, message): void {
    this.warningMessage = { show, message };
  }

  private displayWarningMessage(msg): void {
    this.showBanner = true;
    this.messageBody = msg;
  }

  private closeFeeSheduleModelWindow(): void {
    this.showPricingWindow = false;
  }

  private hidePricingWindow(hidePricingWindowObj: any): void {
    if (hidePricingWindowObj.hidePricingWindow === true) {
      this.showPricingWindow = false;
    }
  }

  private goBackAndEdit(editPricingMegaObj: any): void {
    if (editPricingMegaObj.goBackAndEdit === true) {
      this.showPricingWindow = false;
      this.goBack();
    }
  }

  public closeCommitErrorBanner(): void {
    this.showWarningMessage(false, '');
  }

  public lookupForEarlyCloseFlag() {
    this.megaPricingUpdateList.forEach((item) => {
      if (item.earlyCloseFlag) {
        this.showEarlyCloseLegendPricingSummary = true;
      }
    });
  }

  public checkDisclaimerStatus() {
    if (this.feeDisclaimerService.needsDisclaimer(this.calcFeeRes.prefixCode)) {
      this.showDisclaimer = true;
      this.disclaimerMessage = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
    }
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
